import { FC } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import { noop } from 'lodash';
import cn from 'classnames';
import { useBoolean } from 'usehooks-ts';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  BackToRoute,
  ComponentSpinner,
  HoldsAndCheckoutsMessage,
  PageErrorMessage,
} from '@app/components';
import { useProjectDetails, useSavedSearchesFetch } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import { useBatchInstancesFetch } from '../useBatchInstancesFetch';
import { HistoryRecordsPageWrapper } from '../HistoryRecordsPageWrapper';
import { EditProjectModal } from '../ProjectModals';
import { ProjectDetailsHeroSection } from './ProjectDetailsHeroSection';

import './ProjectDetails.scss';

const cnBem = createBemBlockBuilder(['projectDetails']);

export const ProjectsDetailsPage: FC = () => {
  const { $t } = useIntl();
  const { id: projectId = '' } = useParams();
  const projectDetailsQuery = useProjectDetails({ projectId });
  const projectRecordsQuery = useBatchInstancesFetch({
    queryKey: 'projectRecords',
    list: projectDetailsQuery?.data?.projectInstances,
  });
  const savedSearchesQuery = useSavedSearchesFetch();
  const {
    value: isEditProjectModalOpen,
    setTrue: openEditProjectModal,
    setFalse: closeEditProjectModal,
  } = useBoolean(false);

  if (
    projectDetailsQuery.isLoading ||
    (projectRecordsQuery.isLoading &&
      projectRecordsQuery.fetchStatus !== 'idle' &&
      !projectDetailsQuery.isError)
  ) {
    return <ComponentSpinner />;
  }

  if (projectDetailsQuery.isError) {
    return <PageErrorMessage onClick={projectDetailsQuery.refetch} />;
  }

  return (
    <div
      className={cn(cnBem(), 'container')}
      data-auto="projects-details-container"
    >
      <BackToRoute text="Projects" to={ROUTE.projects} />
      <ProjectDetailsHeroSection
        title={projectDetailsQuery.data.name}
        description={projectDetailsQuery.data.description}
        openEditProjectModal={openEditProjectModal}
      />
      <HistoryRecordsPageWrapper
        parentRoute={ROUTE.projectsDetails}
        isSearchesTabFirst={false}
        //TODO: delete it when we will work on empty state for projects page
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        searchesTabConfig={{
          title: $t(sharedMessages.searches),
          documentTitle: $t({
            id: 'projects.searchHistoryDocumentTitle',
            defaultMessage: 'Searches - Projects - EBSCO Locate',
          }),
          itemSubtitle: $t(sharedMessages.search),
          route: generatePath(ROUTE.projectsDetailsSearches, {
            id: projectId,
          }) as ROUTE,
          list: projectDetailsQuery.data.projectSearches,
          savedSearchesList: savedSearchesQuery.data,
          onEntryRemoval: noop,
        }}
        //TODO: delete it when we will work on empty state for projects page
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        recordsTabConfig={{
          title: $t(sharedMessages.records),
          documentTitle: $t({
            id: 'projects.viewedRecordsDocumentTitle',
            defaultMessage: 'Records - Projects - EBSCO Locate',
          }),
          route: generatePath(ROUTE.projectsDetailsRecords, {
            id: projectId,
          }) as ROUTE,
          list: projectRecordsQuery.data,
          isError: projectRecordsQuery.isError,
          actionButton: (
            <Button>
              <HoldsAndCheckoutsMessage
                requestsMessage={$t(sharedMessages.requestItem)}
                holdsMessage={$t(sharedMessages.placeHold)}
              />
            </Button>
          ),
          refetch: projectRecordsQuery.refetch,
          onEntryRemoval: noop,
        }}
      />
      <EditProjectModal
        isOpen={isEditProjectModalOpen}
        id={projectId}
        values={{
          name: projectDetailsQuery.data.name,
          description: projectDetailsQuery.data.description,
        }}
        onClose={closeEditProjectModal}
      />
    </div>
  );
};
